import { Button, Callout, Modal } from '@dev-spendesk/grapes';
import React, { useMemo, useState } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';

type DeletionStep = 'first' | 'second';

type Props = {
  isOpen: boolean;
  showMappedFieldSubtitle: boolean;
  onClose(): void;
  onSubmit(): void;
};

export const CustomFieldDeletionModal = ({
  isOpen,
  showMappedFieldSubtitle,
  onClose,
  onSubmit,
}: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const [step, setStep] = useState<DeletionStep>('first');

  const isFirstStep = useMemo(() => step === 'first', [step]);

  let subtitle;
  if (step === 'second') {
    subtitle = t('customFields.deletion.confirmationSubtitle');
    if (showMappedFieldSubtitle) {
      subtitle = t('customFields.deletion.mappedFieldConfirmation', {
        integration: accountingSoftwareName,
      });
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      iconVariant="alert"
      title={t('customFields.deletion.title')}
      subtitle={subtitle}
      iconName="hexagone-cross"
      actions={[
        <Button
          key="cancel-button"
          text={t('customFields.deletion.cancel')}
          type="button"
          variant="secondaryNeutral"
          onClick={onClose}
        />,
        isFirstStep ? (
          <Button
            key="continue-button"
            text={t('customFields.deletion.continue')}
            iconName="chevron-right"
            iconPosition="right"
            type="button"
            variant="primaryAlert"
            onClick={() => setStep('second')}
          />
        ) : (
          <Button
            key="submit-button"
            text={t('customFields.deletion.submit')}
            type="button"
            variant="primaryAlert"
            onClick={() => onSubmit()}
          />
        ),
      ]}
    >
      {isFirstStep ? (
        t('customFields.deletion.description')
      ) : (
        <DeletionDetails />
      )}
    </Modal>
  );
};

const DeletionDetails = () => {
  const { t } = useTranslation('global');
  return (
    <div className="mt-16 flex flex-col gap-8">
      <Callout
        variant="alert"
        className="mx-8"
        title={t('customFields.deletion.warning.preparedPayables')}
      />
      <Callout
        variant="alert"
        className="mx-8"
        title={t('customFields.deletion.warning.exportTemplates')}
      />
      <Callout
        variant="alert"
        className="mx-8"
        title={t('customFields.deletion.warning.approvedAndPendingRequests')}
      />
      <Callout
        variant="alert"
        className="mx-8"
        title={t('customFields.deletion.warning.approvedSubscriptions')}
      />
      <Callout
        variant="success"
        className="mx-8"
        title={t('customFields.deletion.warning.unaffectedPayables')}
      />
    </div>
  );
};
